import React, { useEffect, useState, useContext, useRef } from "react";
import { LuChevronsLeftRight, LuChevronDown } from "react-icons/lu";
import { MdClose } from "react-icons/md";
import "./ModuleWindow.css";
import { GeneralContext } from "../../GeneralProvider";

let highestZIndex = 999;

export default function ModuleWindow({
  children,
  name,
  updateWindowPosition,
  currentPosition,
  id,
  onClose,
  currentSize,
  projectId,
  independent = false,
}) {
  const {
    selectedProjectID,
    projects,
    openedWindows,
    setOpenedWindows,
    handleUpdateOpenedWindows,
    setShChannelUri,
    setDmRoomUri,
    setEventIDUri,
  } = useContext(GeneralContext);

  const positionRef = useRef({
    x: window.innerWidth / 2 - 500,
    y: window.innerHeight / 2 - 350,
  });
  const [position, setPosition] = useState(positionRef.current);
  const [size, setSize] = useState({ width: 1000, height: 750 });
  const [isOpen, setIsOpen] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [previousState, setPreviousState] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationType, setAnimationType] = useState("");
  const [zIndex, setZIndex] = useState(highestZIndex);

  useEffect(() => {
    const onZIndexChange = (e) => {
      if (e.detail.id === id) {
        setZIndex(e.detail.zIndex);
      }
    };
    window.addEventListener("mw:zindex-change", onZIndexChange);
    return () => window.removeEventListener("mw:zindex-change", onZIndexChange);
  }, [id]);

  // ---- Fullscreen counter helpers (global) ----
  const getFsCount = () => Number(document.body.dataset.mwFsCount || 0);
  const setFsCount = (n) => {
    document.body.dataset.mwFsCount = String(Math.max(0, n));
    // Body class yönetimi
    if (n > 0) document.body.classList.add("mw-fullscreen");
    else document.body.classList.remove("mw-fullscreen");
    // Dinleyicilere haber ver
    window.dispatchEvent(new CustomEvent("mw:fullscreen-change", { detail: { count: n } }));
  };
  const incFs = () => setFsCount(getFsCount() + 1);
  const decFs = () => setFsCount(getFsCount() - 1);

  // Bu pencerenin dock/context içindeki kaydı
  const thisWin = openedWindows?.find?.((w) => w.id === id);

  // isFullScreen <-> openedWindows.isFullScreen senkronu (varsa)
  useEffect(() => {
    if (!thisWin) return;
    if (thisWin.isFullScreen && !isFullScreen) {
      setIsFullScreen(true);
    } else if (!thisWin.isFullScreen && isFullScreen) {
      setIsFullScreen(false);
    }
  }, [thisWin?.isFullScreen]);

  // isFullScreen state değişiminde FS counter'ı yönet
  const wasFullRef = useRef(false);
  useEffect(() => {
    if (isFullScreen && !wasFullRef.current) {
      wasFullRef.current = true;
      incFs();
    } else if (!isFullScreen && wasFullRef.current) {
      wasFullRef.current = false;
      decFs();
      // Peek'i hemen kapatmak istersen: window.dispatchEvent(new Event("mw:exit-fullscreen"));
    }
  }, [isFullScreen]);

  // KOMPONENT KAPANIRKEN: FS ise sayacı azalt
  useEffect(() => {
    return () => {
      if (wasFullRef.current) {
        wasFullRef.current = false;
        decFs();
      }
    };
  }, []);

  // openedWindows ile isFullScreen durumunu senkronize et (Home'dan dönünce FS'yi koru)
  useEffect(() => {
    if (!thisWin) return;
    const hh = 64;
    if (thisWin.isFullScreen) {
      document.body.classList.add("mw-fullscreen");
      // previousState boşsa preFS'ten doldur
      if (!previousState && (thisWin.preFSPosition || thisWin.preFSSize)) {
        setPreviousState({
          position: thisWin.preFSPosition || position,
          size: thisWin.preFSSize || size,
        });
      }
      setPosition({ x: 0, y: hh });
      setSize({ width: window.innerWidth, height: window.innerHeight - hh });
      if (!isFullScreen) setIsFullScreen(true);
    } else {
      document.body.classList.remove("mw-fullscreen");
      // FS değil -> previousState varsa eski haline dön; yoksa dokunma
      if (previousState) {
        setPosition(previousState.position);
        setSize(previousState.size);
      }
      if (isFullScreen) setIsFullScreen(false);
    }
  }, [thisWin?.isFullScreen, thisWin?.isActive]);

  useEffect(() => {
    setZIndex(++highestZIndex);

    if (currentPosition) {
      setPosition(currentPosition);
    }
  }, [currentPosition]);

  useEffect(() => {
    const onResize = () => {
      if (isFullScreen) {
        setPosition({ x: 0, y: 0 });
        setSize({ width: window.innerWidth, height: window.innerHeight });
        setOpenedWindows((prev) => {
          const updated = prev.map((win) =>
            win.id === id
              ? {
                ...win,
                position: { x: 0, y: 0 },
                size: { width: window.innerWidth, height: window.innerHeight },
              }
              : win
          );
          handleUpdateOpenedWindows(updated);
          return updated;
        });
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isFullScreen, id, handleUpdateOpenedWindows, setOpenedWindows]);

  useEffect(() => {
    if (currentPosition) {
      setPosition(currentPosition);
    }
    if (currentSize) {
      setSize(currentSize);
    }
  }, []);

  if (!independent) {
    if (
      (!projectId || projectId !== selectedProjectID) &&
      projects?.length !== 0
    ) {
      return null;
    }
  }

  const onModalSelected = (e) => {
    if (e.target.closest(".modal-header")) return;

    if (zIndex < highestZIndex) {
      setZIndex(++highestZIndex);
    }

    const modalAlreadySelected = openedWindows.find(
      (win) => win.id === id && win.isSelected
    );

    if (!modalAlreadySelected) {
      const updatedWindows = openedWindows.map((win) =>
        win.id === id
          ? { ...win, isSelected: true }
          : { ...win, isSelected: false }
      );

      setOpenedWindows(updatedWindows);

      // Servise sadece gerekli durumlarda güncelleme gönder
      handleUpdateOpenedWindows(updatedWindows);
    }
  };

  const handleMouseDown = (e) => {
    // Tam ekran modunda sürüklemeyi devre dışı bırak
    if (isFullScreen) return;

    const modal = e.target.closest(".modal");
    const shiftX = e.clientX - modal.getBoundingClientRect().left;
    const shiftY = e.clientY - modal.getBoundingClientRect().top;

    const onMouseMove = (event) => {
      let newX = event.clientX - shiftX;
      let newY = event.clientY - shiftY;

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const { width, height } = size;
      newX = Math.max(0, Math.min(newX, screenWidth - width));
      newY = Math.max(0, Math.min(newY, screenHeight - height));

      const newPosition = { x: newX, y: newY };
      setPosition(newPosition);
      positionRef.current = newPosition;
      updateWindowPosition(id, newPosition);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      const finalPosition = positionRef.current;
      setOpenedWindows((prev) => {
        const updatedWindows = prev.map((win) =>
          win.id === id ? { ...win, position: finalPosition } : win
        );
        handleUpdateOpenedWindows(updatedWindows);
        return updatedWindows;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleResizeMouseDown = (e, direction) => {
    e.stopPropagation();
    const startWidth = size.width;
    const startHeight = size.height;
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosition = { ...position };

    const onMouseMove = (event) => {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newPosition = { ...startPosition };

      if (direction.includes("right")) {
        newWidth = startWidth + dx;
      }

      if (direction.includes("bottom")) {
        newHeight = startHeight + dy;
      }

      if (direction.includes("left")) {
        newWidth = startWidth - dx;
        newPosition.x = startPosition.x + dx;
      }

      if (direction.includes("top")) {
        newHeight = startHeight - dy;
        newPosition.y = startPosition.y + dy;
      }

      const updatedSize = {
        width: Math.max(300, newWidth),
        height: Math.max(200, newHeight),
      };

      setSize(updatedSize);
      setPosition(newPosition);

      updateWindowPosition(id, newPosition, updatedSize);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleOnClose = () => {
    if (isFullScreen) {
      setIsFullScreen(false)
    }
    else {
      // fullscreen değilse sayacı etkileme
      const fsCount = Number(document.body.dataset.mwFsCount || 0);
      window.dispatchEvent(new CustomEvent("mw:fullscreen-change", { detail: { count: fsCount } }));

    }
    setOpenedWindows((prev) => prev.filter((win) => win.id !== id));
    setIsOpen(false);
    onClose();
    setShChannelUri("")
    setDmRoomUri("")
    setEventIDUri("")
  };

  const handleToggleFullScreen = () => {
    setAnimationType("fullscreen-transition");
    setIsAnimating(true);

    setTimeout(() => {
      setIsAnimating(false);
      setAnimationType("");
    }, 300);

    setOpenedWindows((prev) => {
      const updatedWindows = prev.map((win) =>
        win.id === id
          ? isFullScreen
            ? {
              ...win,
              position: previousState?.position ?? win.preFSPosition ?? win.position,
              size: previousState?.size ?? win.preFSSize ?? win.size,
              isFullScreen: false,
            }
            : {
              ...win,
              position: { x: 0, y: 64 }, // üst boşluk yok
              size: {
                width: window.innerWidth,   // tam genişlik
                height: window.innerHeight, // alt boşluk yok
              },
              preFSPosition: previousState?.position ?? position,
              preFSSize: previousState?.size ?? size,
              isFullScreen: true,
            }
          : win
      );

      handleUpdateOpenedWindows(updatedWindows);
      return updatedWindows;
    });

    if (isFullScreen) {
      // ----- FS -> normal
      document.body.classList.remove("mw-fullscreen");
      window.dispatchEvent(new Event("mw:exit-fullscreen"));
      // Güvenli fallback zinciri:
      const fallbackSize = { width: 1000, height: 750 };
      const fallbackPos = {
        x: Math.max(0, (window.innerWidth - fallbackSize.width) / 2),
        y: Math.max(64, (window.innerHeight - 64 - fallbackSize.height) / 2 + 64),
      };
      const prevPos = previousState?.position ?? thisWin?.preFSPosition ?? fallbackPos;
      const prevSize = previousState?.size ?? thisWin?.preFSSize ?? fallbackSize;
      setPosition(prevPos);
      setSize(prevSize);
      setIsFullScreen(false);
    } else {
      // ----- normal -> FS
      document.body.classList.add("mw-fullscreen");
      setPreviousState({ position, size }); // toggle sırasındaki gerçek preFS
      const hh = 64;
      setPosition({ x: 0, y: hh });
      setSize({ width: window.innerWidth, height: window.innerHeight - hh });
      setIsFullScreen(true);
    }
  };

  const handleMinimize = () => {
    setAnimationType("minimizing");
    setIsAnimating(true);
    window.dispatchEvent(new Event("mw:exit-fullscreen"));
    setTimeout(() => {
      setOpenedWindows((prev) => {
        const updatedWindows = prev.map((win) =>
          win.id === id ? { ...win, isActive: false, isSelected: false } : win
        );

        // Servise güncel windows listesini gönder
        handleUpdateOpenedWindows(updatedWindows);

        return updatedWindows;
      });

      setIsAnimating(false);
    }, 300);
  };

  function normalizeUnicodeEscapes(input) {
    if (input == null) return "";
    let s = String(input);
    for (let i = 0; i < 4; i++) {
      const prev = s;
      s = s.replace(/%u([0-9a-f]{4})/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16))
      );
      try {
        if (/%[0-9a-f]{2}/i.test(s)) s = decodeURIComponent(s);
      } catch { }
      s = s.replace(/\\\\u([0-9a-f]{4})/gi, "\\u$1");
      s = s.replace(/\\u([0-9a-f]{4})/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16))
      );
      s = s.replace(/(^|[^\\])u([0-9a-f]{4})/gi, (_, p, h) =>
        p + String.fromCharCode(parseInt(h, 16))
      );
      s = s.replace(/\\x([0-9a-f]{2})/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16))
      );
      s = s.replace(/&#x([0-9a-f]{1,6});/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16))
      );
      s = s.replace(/&#(\d+);/g, (_, d) =>
        String.fromCharCode(parseInt(d, 10))
      );

      if (s === prev) break;
    }
    return s;
  }

  return (
    <div onClick={onModalSelected}>
      {isOpen && (
        <div
          className={`modal ${isAnimating ? animationType : ""} ${isFullScreen ? "is-fullscreen" : ""}`}
          style={{
            top: `${position.y}px`,
            left: `${position.x}px`,
            width: `${size.width}px`,
            height: `${size.height}px`,
            display: "flex",
            flexDirection: "column",
            zIndex: zIndex,
          }}
        >
          <div
            className="modal-header"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleToggleFullScreen}
          >
            <span className="modal-title">
              <div className="light-dot"></div>
              {normalizeUnicodeEscapes(name)}
            </span>
            <div className="close-button-container">
              <div className="go-down-button" onClick={handleMinimize}>
                <LuChevronDown />
              </div>
              <div className="resize-button" onClick={handleToggleFullScreen}>
                <LuChevronsLeftRight />
              </div>
              <div className="close-button" onClick={handleOnClose}>
                <MdClose />
              </div>
            </div>
          </div>
          <div className="modal-content">
            {" "}
            {typeof children === "function"
              ? React.createElement(children)
              : children}
          </div>
          <div
            className="resize-handle left"
            onMouseDown={(e) => handleResizeMouseDown(e, "left")}
          />
          <div
            className="resize-handle top-left"
            onMouseDown={(e) => handleResizeMouseDown(e, "top-left")}
          />
          <div
            className="resize-handle top"
            onMouseDown={(e) => handleResizeMouseDown(e, "top")}
          />
          <div
            className="resize-handle top-right"
            onMouseDown={(e) => handleResizeMouseDown(e, "top-right")}
          />
          <div
            className="resize-handle right"
            onMouseDown={(e) => handleResizeMouseDown(e, "right")}
          />
          <div
            className="resize-handle bottom-left"
            onMouseDown={(e) => handleResizeMouseDown(e, "bottom-left")}
          />
          <div
            className="resize-handle bottom"
            onMouseDown={(e) => handleResizeMouseDown(e, "bottom")}
          />
          <div
            className="resize-handle bottom-right"
            onMouseDown={(e) => handleResizeMouseDown(e, "bottom-right")}
          />
        </div>
      )}
    </div>
  );
}
